/**
 * Manages operations related to asset fields.
 * @class
 * @requires jQuery
 */
import { removeHost } from '../methods/removeHost.js';

export class AssetField {
    /**
     * AssetField クラスを生成するためのコンストラクタ。
     *
     * @class
     * @param {string} id - アセットフィールドのID。この引数は必須です。
     * @param {string} [type='image'] - アセットのタイプ。デフォルトは 'image' です。
     *
     * @throws {Error} id パラメータが指定されていない場合、エラーをスローします。
     */
    constructor(id, type = 'image', handle = '') {
        if (!id) {
            throw new Error('The "id" parameter is required as the first argument.');
        }
        this.id = id;
        this.type = type;
        this.handle = handle;
    }

    //----------------------------------------
    // 静的メソッド
    //----------------------------------------

    /**
     * AssetField クラスの初期化メソッド。
     * mtModal() を使用してモーダルを初期化し、'hidden.bs.modal.assetField' イベントのハンドラを設定します。
     *
     * @static
     */
    static init() {
        // アセットモーダルの中の処理
        AssetField.initAssetModalInnerContent();
        if (typeof window.top.mtappVars.__system.assetField === 'object') {
            return;
        }
        window.mtappVars.__system.assetField = window.mtappVars.__system.assetField || { uploadDirectories: {} };
        jQuery('#mtapp-asset-field').mtModal();
        const mtModal = document.querySelector('.mt-modal');
        mtModal.addEventListener('shown.bs.modal', AssetField.handleModalShown);
        mtModal.addEventListener('hidden.bs.modal', AssetField.handleModalHidden);
    }

    /**
     * アセットモーダルの中のiframeの中の処理
     */
    static initAssetModalInnerContent() {
        const params = new URLSearchParams(location.search);
        if (
            window.top !== window &&
            params.get('__mode') === 'list_asset' &&
            params.get('_type') === 'asset' &&
            params.get('dialog_view') === '1'
        ) {
            // モーダルの中のiframeが読み込まれた時の処理
            window.addEventListener('load', () => {
                let clickedButton = '';
                const insertButton = document.querySelector('button[type="submit"].btn-primary');
                if (insertButton) {
                    insertButton.addEventListener('click', () => {
                        clickedButton = 'insert';
                    });
                }

                const cancelButton = document.querySelector('button[type="button"].cancel');
                if (cancelButton) {
                    cancelButton.addEventListener('click', () => {
                        clickedButton = 'cancel';
                    });
                }

                const selectAssetForm = document.getElementById('select_asset');
                if (selectAssetForm) {
                    const selectAssetId = selectAssetForm.querySelector('input[name="id"]');
                    window.addEventListener('unload', () => {
                        const selectedValue = selectAssetId.value;
                        if (clickedButton === 'insert' && selectedValue) {
                            window.top.mtappVars.__system.assetField.assetId = selectAssetId.value;
                            const assetJson = window.document.getElementById(`asset-${selectedValue}-json`);
                            if (assetJson) {
                                window.top.mtappVars.__system.assetField.assetDetails = JSON.parse(assetJson.value);
                            } else {
                                const uploadProgress = window.document.getElementById(`upload-progress`);
                                if (uploadProgress) {
                                    const uploadedAsset = uploadProgress.querySelector(`[data-asset-id="${selectedValue}"]`);
                                    if (uploadedAsset) {
                                        const assetLink = uploadedAsset.querySelector('a[target="_blank"]');
                                        if (assetLink) {
                                            const href = assetLink.getAttribute('href');
                                            window.top.mtappVars.__system.assetField.assetDetails = {
                                                url: href
                                            };
                                        }
                                    }
                                }
                            }
                        } else {
                            window.top.mtappVars.__system.assetField.assetId = '';
                        }
                    });
                }
                jQuery(document).on('mtappAssetFieldReady', function (event, data) {
                    const handle = data.mtappMultiFieldHandle;
                    const handleDir = window.top.mtappVars.__system.assetField.uploadDirectories[handle];
                    const extraPath = window.document.getElementById('extra_path');
                    if (!handle || typeof handleDir === 'undefined' || !extraPath) {
                        return;
                    }
                    if (typeof handleDir === 'string') {
                        extraPath.value = handleDir;
                    } else if (Array.isArray(handleDir)) {
                        // 新しい select 要素を作成
                        const selectElement = document.createElement('select');
                        selectElement.name = 'extra_path_select';
                        selectElement.classList.add('form-select');

                        // オプションを select に追加
                        handleDir.forEach(function (optionValue) {
                            const option = document.createElement('option');
                            option.value = optionValue;
                            option.text = optionValue;
                            selectElement.appendChild(option);
                        });

                        // input の直前に select を挿入
                        extraPath.parentNode.insertBefore(selectElement, extraPath);

                        // 元の input 要素を非表示にする
                        extraPath.style.display = 'none';

                        // selectElement が変更された時に選択された値を extraPath の value にセットするイベントリスナーを追加
                        selectElement.addEventListener('change', function () {
                            extraPath.value = this.value;
                        });

                        // 初期値を設定するために、イベントリスナー関数を手動で一度実行する
                        selectElement.dispatchEvent(new Event('change'));
                    }
                });
                jQuery(document).trigger('mtappAssetFieldReady', [{ mtappMultiFieldHandle: params.get('mtapp_mf_handle') }]);
            });
        }
    }
    /**
     * 指定された要素の値を設定し、'input' と 'blur' イベントをディスパッチします。
     *
     * @static
     * @param {HTMLInputElement} el - 値を設定する対象のDOM要素。
     * @param {string|number} value - 要素に設定する値。
     */
    static setElementValue(el, value) {
        if (el) {
            el.value = value;
            el.dispatchEvent(new Event('input'));
            el.dispatchEvent(new Event('blur'));
        }
    }

    /**
     * 指定されたURLからデータを非同期的に取得する。
     *
     * @static
     * @param {string} url - データを取得するためのAPIのURL。
     * @param {Object} [params={}] - APIリクエストに付随するパラメータ。
     * @returns {Promise} データの取得結果を保持するPromiseオブジェクト。
     */
    static async fetchDataAPI(url, params) {
        const queryParams = new URLSearchParams(params).toString();
        const response = await fetch(`${url}?${queryParams}`);
        if (!response.ok) {
            throw new Error(`Network response was not ok: ${response.statusText}`);
        }
        return response.json();
    }

    /**
     * `mtappVars.__system.assetField`のプロパティを初期状態にリセットする。
     *
     * @static
     */
    static cleanup() {
        window.mtappVars.__system.assetField.assetId = '';
        window.mtappVars.__system.assetField.assetDetails = null;
        // window.mtappVars.__system.assetField.assetType = 'image';
    }

    /**
     * モーダルが表示された際のハンドラー。
     *
     * @static
     */
    static handleModalShown() {
        AssetField.cleanup();
    }

    /**
     * モーダルが非表示になった際のハンドラー。
     * AssetField オブジェクトの情報を元に、関連するエレメントの値を更新したり、DataAPIから情報を取得します。
     *
     * @static
     */
    static handleModalHidden() {
        const assetFieldObj = window.top.mtappVars.__system.assetField;
        if (assetFieldObj.assetId === '') return;

        const targetIdElement = document.getElementById(assetFieldObj.fieldId);
        const updateItemIdButton = targetIdElement.previousElementSibling;
        AssetField.setElementValue(targetIdElement, assetFieldObj.assetId);

        const targetThumbElement = document.getElementById(assetFieldObj.fieldId + '-thumb');
        const targetUrlElement = document.getElementById(assetFieldObj.fieldId + '-url');

        // すでにアップロードされているアセットから選択された場合
        const assetDetails = assetFieldObj.assetDetails;
        if (assetDetails) {
            AssetField.setElementValue(targetUrlElement, removeHost(assetDetails.url));
        } else {
            // アセットのURLを取得
            if (!window.mtappVars.DataAPI) {
                alert('DataAPI is not available.');
                console.error('DataAPI is not available.');
                return;
            }
            window.mtappVars.DataAPI.getAsset(window.mtappVars.blog_id, assetFieldObj.assetId, (response) => {
                if (response.error) {
                    console.error('Error fetching data:', response.error.message);
                }
                AssetField.setElementValue(targetUrlElement, removeHost(response.url));
            });
        }
        // 画像の場合のサムネイルの処理
        if (assetFieldObj.fieldId && assetFieldObj.assetType === 'image') {
            if (!window.mtappVars.DataAPI) {
                alert('DataAPI is not available.');
                console.error('DataAPI is not available.');
                return;
            }
            window.mtappVars.DataAPI.assetInfo(assetFieldObj.assetId, (assetInfo) => {
                if (assetInfo.error) {
                    console.error('Error fetching data:', assetInfo.error.message);
                    return;
                }
                const assetSiteId = assetInfo.siteId;
                window.mtappVars.DataAPI.getThumbnail(assetSiteId, assetFieldObj.assetId, { width: 120, height: 120 }, (response) => {
                    if (response.error) {
                        console.error('Error fetching data:', response.error.message);
                    }
                    AssetField.setElementValue(targetThumbElement, removeHost(response.url));
                    if (updateItemIdButton instanceof HTMLElement) {
                        updateItemIdButton.click();
                    }
                });
            });
        } else if (updateItemIdButton instanceof HTMLElement) {
            updateItemIdButton.click();
        }
        delete assetFieldObj.fieldId;
    }

    //----------------------------------------
    // インスタンスプロパティ
    //----------------------------------------

    id = '';
    type = 'image';
    handle = '';

    //----------------------------------------
    // インスタンスメソッド
    //----------------------------------------

    /**
     * アセットを選択するためのメソッド。
     * 指定されたIDとタイプに基づいてアセットを選択し、適切なURLをセットしてクリックイベントをトリガーします。
     * @method
     * @this AssetField
     */
    select() {
        window.mtappVars.__system.assetField.fieldId = this.id;
        window.mtappVars.__system.assetField.assetType = this.type;

        const triggerElement = document.getElementById('mtapp-asset-field');
        const href = triggerElement.getAttribute('href');
        let url;
        if (href.startsWith('http://') || href.startsWith('https://')) {
            // hrefが完全なURLの場合
            url = new URL(href);
        } else {
            // hrefが相対URLの場合
            url = new URL(href, window.location.origin);
        }

        // パラメータの変更や削除の処理
        if (this.type === 'image') {
            url.searchParams.set('filter', 'class');
            url.searchParams.set('filter_val', 'image');
            url.searchParams.set('require_type', 'image');
        } else {
            url.searchParams.delete('filter');
            url.searchParams.delete('filter_val');
            url.searchParams.delete('require_type');
        }
        url.searchParams.set('mtapp_mf_handle', this.handle);

        // 変更されたURLをhref属性に設定
        triggerElement.setAttribute('href', url.toString());
        triggerElement.click();
        return false;
    }

    /**
     * Removes an asset with a specified ID.
     * @param {string} id - ID of the asset to be removed.
     */
    remove() {
        const idField = document.getElementById(this.id);
        AssetField.setElementValue(idField, '');

        const thumbField = document.getElementById(this.id + '-thumb');
        AssetField.setElementValue(thumbField, '');

        const urlField = document.getElementById(this.id + '-url');
        AssetField.setElementValue(urlField, '');
    }
}
